import { useCallback, useMemo } from "react";

import { Category, CategoryType } from "../components/SearchTags/SearchTags";
import { useAppSelector } from "../redux/hooks";
import { useGetMapConfigQuery } from "../redux/services/vectormapsAPI";

import { parseLanguageObject } from "../utils/parseLanguageObject";
import { isLTR } from "../utils/getLanguageDir";

export const useQuickSearchTags = () => {
  const { language, defaultLanguage } = useAppSelector(
    (state) => state.application,
  );

  const { data: mapData } = useGetMapConfigQuery();

  const getQuickSearchTags = useCallback(
    (defaultLanguageFlag = false) => {
      const st =
        mapData?.search_tags.map(
          (tag) =>
            ({
              id: tag.id,
              name: parseLanguageObject(
                tag.name,
                defaultLanguageFlag ? defaultLanguage : language,
              ),
              type: CategoryType.NORMAL,
            }) as Category,
        ) || [];
      const bt =
        mapData?.search_tags_bespoke.map(
          (tag) =>
            ({
              id: tag.id,
              name: parseLanguageObject(
                tag.name,
                defaultLanguageFlag ? defaultLanguage : language,
              ),
              type: CategoryType.BESPOKE,
            }) as Category,
        ) || [];
      return st.concat(bt);
    },
    [
      language,
      defaultLanguage,
      mapData?.search_tags,
      mapData?.search_tags_bespoke,
    ],
  );

  const quickSearchTags = useMemo(() => {
    return getQuickSearchTags();
  }, [getQuickSearchTags]);

  const defaultLanguageQuickSearchTags = useMemo(() => {
    return getQuickSearchTags(true);
  }, [getQuickSearchTags]);

  const getQuickSearchTagFromId = (
    id: string | number,
    type: CategoryType,
    defaultLanguageFlag = false, // if true, will get the tag name based on default language instead of current language
  ) =>
    (defaultLanguageFlag
      ? defaultLanguageQuickSearchTags
      : quickSearchTags
    ).find((tag) => String(tag.id) === String(id) && tag.type === type)?.name ||
    "";

  // quickSearchTags are reversed for RTL languages
  return {
    quickSearchTags: isLTR() ? quickSearchTags : [...quickSearchTags].reverse(),
    getQuickSearchTagFromId,
  };
};
